import { animated } from "@react-spring/web";
import useColors from "../../../../../../../api/hooks/useColors";
import useChartSeries from "./chartSeries";

export const CUSTOM_BAR_WIDTH = 32;

const SLAChartCustomBar = (barProps) => {
  const colors = useColors();
  const series = useChartSeries();

  // Extract props from the provided barProps object
  const props = barProps;

  // Find the matching series configuration to get styling information
  const seriesItem = series.find((item) => item.id === props?.ownerState?.id);
  const isWithinSla = seriesItem?.isWithinSla || false;

  // Extract styling from props and seriesItem
  const barColor = seriesItem?.color || props?.style?.fill;

  // Check if the height is 0, and if so, don't render anything
  const currentHeight = props?.style?.height.get();
  if (currentHeight === 0) {
    return null;
  }

  // Calculate x position to center the bar with additional spacing
  const originalWidth = props?.style?.width.get();
  // For more spacing, we reduce the effective width we use
  const effectiveWidth = originalWidth * 0.4; // This creates more spacing between bar groups (reduced from 0.6)
  const xCenterOffset = (effectiveWidth - CUSTOM_BAR_WIDTH) / 2;
  // Adjust x position to account for the stack position
  const stackPosition = props?.ownerState?.stackedClusterPosition || 0;
  const barPositionOffset = stackPosition * 16; // 16px spacing between stacks

  const adjustedX =
    props?.style?.x.get() +
    (originalWidth - effectiveWidth) / 2 +
    xCenterOffset +
    barPositionOffset;

  const rectProps = {
    x: props?.style?.x.to(() => adjustedX),
    y: isWithinSla ? props?.style?.y.to((y) => y + 2) : props?.style?.y,
    width: CUSTOM_BAR_WIDTH,
    height: isWithinSla
      ? props?.style?.height.to((h) => h - 2)
      : props?.style?.height,
    rx: 6,
    ry: 6,
    fill: barColor,
  };

  return (
    <animated.g>
      <animated.path
        d={props?.style?.height.to((height) => {
          if (height === 0) return "";

          const x = adjustedX;
          const y = isWithinSla
            ? props?.style?.y.get() + 2
            : props?.style?.y.get();
          const width = CUSTOM_BAR_WIDTH;

          // Set different radius based on isWithinSla
          const bottomR = isWithinSla ? 6 : 3; // Bottom radius
          const topR = isWithinSla ? 3 : 6; // Top radius

          // Adjust height for SLA bars to account for the gap
          const adjustedHeight = isWithinSla ? height - 2 : height;

          return `
          M${x},${y + topR}
          a${topR},${topR} 0 0 1 ${topR},${-topR}
          h${width - 2 * topR}
          a${topR},${topR} 0 0 1 ${topR},${topR}
          v${adjustedHeight - bottomR - topR}
          a${bottomR},${bottomR} 0 0 1 ${-bottomR},${bottomR}
          h${-width + 2 * bottomR}
          a${bottomR},${bottomR} 0 0 1 ${-bottomR},${-bottomR}
          v${-(adjustedHeight - bottomR - topR)}
          z
        `;
        })}
        {...rectProps}
      />

      {/* Add top border with black stroke */}
      <animated.path
        d={props?.style?.height.to((height) => {
          if (height === 0) return "";

          const x = adjustedX + 3;
          const y = isWithinSla
            ? props?.style?.y.get() + 2
            : props?.style?.y.get();
          const width = CUSTOM_BAR_WIDTH - 6;

          // Simple horizontal line at the top of the bar
          return `M${x},${y} h${width}`;
        })}
        stroke={isWithinSla ? colors.grey_dark : colors.grey_600}
        strokeWidth={1}
        fill="none"
      />
    </animated.g>
  );
};

export default SLAChartCustomBar;
